 
import React from "react";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { MODULE_IDS } from "../../../../../../../constants/enums";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomShrinkTextField,
  CustomTextField,
} from "../../../../../../../components/formsComponents";

import produce from "immer";
import { clearAutomobileScheduleState } from "../../../../../../../redux/automobileSchedule/automobileScheduleActions";

import { AutoComplete } from "../../../../../../../components/AutoCompleteSearches/AutoComplete";
import {
  COMMON_STATUS,
  csioOtherCoverages,
} from "../../../../../../../constants/constants";
import {
  addCommAutomobileScheduleAAsync,
  fetchCommAutomobileScheduleAAsync,
} from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoScheduleActions";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { fetchCommAutomobileVehicleListAsync } from "../../../../../../../redux/commAutoPolicy/commAutoVehicle/commAutoVehicleActions";
import { ICommAutomobileScheduleA } from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoSchedule.types";
import { FieldSet } from "../../../../../../../components/FieldSet/FieldSet";
import { useDefaultQuote } from "../../../../../../../hooks/useDefaultQuote";
import { useCommAutoVehcileInfo } from "../../hooks/useCommAutovehicleInfo";
import { ExtendableSelect } from "../../../../../../../components/formsComponents/ExtendableSelect";
import { PageContainer } from "../../../../../../../components/container/PageContainer";

interface ICommAutoScheduleAProps {
  customerId?: number;
  customerPolicyId?: number;
  incrementalNumber?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (data: ICommAutomobileScheduleA) => void;
  initialData?: ICommAutomobileScheduleA;
  disableApiCalls?: boolean;
  vehicleNumber?: number;
  isPolicyChange?: boolean;
  isVehicleEnabled?: boolean;
  disableSaveApi?: boolean;
}

export const CommAutomobileScheduleA: React.FC<ICommAutoScheduleAProps> = (
  props
) => {
  const {
    customerId,
    customerPolicyId,
    incrementalNumber,
    onSaveSuccess,
    fromRightPanel = false,
    readOnly = false,
    staticStatusValue,
    created_from,
    initialData,
    disableApiCalls = false,
    vehicleNumber,
    isVehicleEnabled = true,
    isPolicyChange = false,
    disableSaveApi
  } = props;

  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.schedule.scheduleA
  );

  const isUpdate = incrementalNumber ? true : false;

  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const { coverageList, fetchDefaultCoverageByCode } = useDefaultQuote();
  const navigate = useNavigate();

  const { vehcileDropdown, vehiclesList } =
    useCommAutoVehcileInfo(customerPolicyId);

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: initialData ? initialData : data,
    validate: (values) => {
      const errors: any = {};
      if (!values.vehicle_number) {
        errors.vehicle_number = "Vehicle is required.";
      }
      return errors;
    },
    onSubmit: (values) => {
      if (disableSaveApi) {
        onSaveSuccess(values);
      } else {
        if (customerPolicyId && customerId) {
          let statusValue = values.status;
          if (staticStatusValue) {
            statusValue = staticStatusValue;
          }
          setSaveLoading(true);
          dispatch(
            addCommAutomobileScheduleAAsync(
              {
                ...values,
                customer_policy_id: customerPolicyId,
                status: statusValue,
                created_from: created_from || "Coverage",
              },
              (isSuccess, data) => {
                if (isSuccess && data) {
                  onSaveSuccess(data);
                  window.scrollTo(0, 0);
                }
                setSaveLoading(false);
              }
            )
          );
        }
      }
    },
  });

  const handleTextChange =
    (index: number, key: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = e.target.value;
      });
      setValues(newValues);
    };

  const handleSelectChange =
    (index: number, key: string) => (selectedOption: any) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = selectedOption
          ? selectedOption
          : "";
      });
      setValues(newValues);
    };

  const handleCoverageChange = async (e: SelectChangeEvent<unknown>) => {
    const coverageCode = e.target.value as string;

    const CoverageInfo = await fetchDefaultCoverageByCode(coverageCode);

    if (CoverageInfo) {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_name = coverageCode;
        draftValues.coverage_details = CoverageInfo.coverage_details;
      });
      setValues(newValues);
    }
  };

  const handleCheckboxChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index].isDeductible = isChecked;
      });
      setValues(newValues);
    };

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({ ...data });
  }, [data]);

  React.useEffect(() => {
    if (
      customerPolicyId &&
      incrementalNumber &&
      vehicleNumber &&
      !disableApiCalls
    ) {
      dispatch(
        fetchCommAutomobileScheduleAAsync(
          customerPolicyId,
          incrementalNumber,
          vehicleNumber
        )
      );
    }
  }, []);

  React.useEffect(() => {
    if (!disableApiCalls) {
      setValues({ ...data });
    }
  }, [data]);

  React.useEffect(() => {
    return () => {
      dispatch(clearAutomobileScheduleState());
    };
  }, []);

  const total = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.premimum) || 0);
    }, 0);
  }, [values.coverage_details]);

  const totalTwo = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.description) || 0);
    }, 0);
  }, [values.coverage_details]);

  const defaultCoverageList = React.useMemo(() => {
    return coverageList.map((item) => {
      return {
        label: `${item.default_coverage_name || "--"} ( ${
          item.default_coverage_code || "N/A"
        } 
          )`,
        value: item.default_coverage_code || "",
      };
    });
  }, [coverageList]);

  const handleVinNumber = async (value: string | number) => {
    const vehicleNumber =  value as number;
    const vinNumber = vehiclesList.find(
      (x) => x.incremental_number === Number(vehicleNumber)
    );

    if (vinNumber) {
      const newValues = produce(values, (draftValues) => {
        draftValues.vehicle_number = vehicleNumber;
        draftValues.vin = vinNumber?.vin || "";
      });
      setValues(newValues);
    }
    else {  const newValues = produce(values, (draftValues) => {
      draftValues.vehicle_number = vehicleNumber;
      draftValues.vin = (vehicleNumber || "").toString();
    });
    setValues(newValues);
  }
  };

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchCommAutomobileVehicleListAsync(customerPolicyId));
    }
  }, []);

  return (
    <PageContainer
       title={isUpdate ? "Edit Vehicle Coverage" : "Add Vehicle Coverage"}
  description="this is inner page"
>

    <PageLoader loading={false}>
      <StandadCard
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Vehicle Coverage Summary" : ""}
        rightHeading={
          !incrementalNumber && (
            <FieldSet disabled={readOnly}>
              <Stack
                direction={"row"}
                spacing={3}
                width={"100%"}
                alignItems={"center"}
              >
                <Typography
                  display={"block"}
                  variant="h6"
                  fontWeight={"bold"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Default Coverage{" "}
                </Typography>
                <ControlledCustomSelect
                  sx={{ width: "300px" }}
                  variant="outlined"
                  size="small"
                  type="string"
                  name="coverage_name"
                  value={values.coverage_name}
                  onChange={handleCoverageChange}
                  displayEmpty
                  fullWidth
                  placeholder="Select one"
                  options={defaultCoverageList}
                ></ControlledCustomSelect>
              </Stack>
            </FieldSet>
          )
        }
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <>
              <Grid container sx={{ mt: 2 }} spacing={2}>
                <Grid item xs={6} md={isPolicyChange ? 6 : 4}>
                  <CustomFormLabel
                    fontWeight={600}
                    variant="h4"
                    textAlign={"center"}
                  >
                    Coverage Number
                  </CustomFormLabel>
                  <CustomTextField
                    name="coverage_number"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.coverage_number}
                    onChange={handleChange}
                    disabled={!isPolicyChange}
                  />
                </Grid>
                {isVehicleEnabled && (
                  <Grid item xs={6} md={isPolicyChange ? 6 : 4}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h4"
                      textAlign={"center"}
                    >
                      Vehicle Number
                    </CustomFormLabel>
                    <ExtendableSelect
                      value={values.vehicle_number}
                      onChange={handleVinNumber}
                      fullWidth
                      placeholder="Select one"
                      options={vehcileDropdown}
                      errorMessage={errors.vehicle_number}
                      error={errors.vehicle_number ? true : false}
                    ></ExtendableSelect>
                  </Grid>
                )}
                {!staticStatusValue && (
                  <Grid item xs={12} md={isPolicyChange ? 6 : 4}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h4"
                      textAlign={"center"}
                    >
                      Status
                    </CustomFormLabel>
                    <ControlledCustomSelect
                      fullWidth
                      value={values.status}
                      name="status"
                      onChange={handleChange}
                      placeholder="Select one"
                      displayEmpty
                      options={COMMON_STATUS}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12}>
                  <TableContainer>
                    <Table
                      aria-label="collapsible table"
                      sx={{
                        whiteSpace: {
                          xs: "nowrap",
                          sm: "nowrap",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h4">
                              Section
                            </Typography>
                          </TableCell>
                          <TableCell align="center" colSpan={3}>
                            <Typography fontWeight={600} variant="h4">
                              Automobile
                            </Typography>
                          </TableCell>
                          <TableCell align="center"></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell />
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Limit
                            </Typography>
                          </TableCell>
                          {/* <TableCell align="center">
                                  <Typography fontWeight={600} variant="h6">
                                    Deductible
                                  </Typography>
                                </TableCell> */}
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Premium
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Occassional Driver Premium
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.coverage_details.map((item, index) => {
                          const showDeductibleOnly = [
                            "Specified Perils (excluding Collision or Upset)",
                            "Comprehensive (excluding Collision or Upset)",
                            "Collision or Upset",
                            "All Perils",
                            "Direct Compensation-Property Damage",
                          ].includes(item.label);

                          return (
                            <React.Fragment key={item.key}>
                              {item.label ===
                                "Family Protection Coverage - OPCF 44R" && (
                                <TableRow>
                                  <TableCell>
                                    <Typography
                                      fontWeight={600}
                                      variant="h5"
                                      sx={{ padding: "8px 0" }}
                                    >
                                      Policy Change Forms (Name & No.)
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible/Limit
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              {item.label ===
                                "Specified Perils (excluding Collision or Upset)" && (
                                <TableRow>
                                  <TableCell>
                                    <Typography
                                      fontWeight={600}
                                      variant="h5"
                                      sx={{ padding: "8px 0" }}
                                    >
                                      LOSS OR DAMAGE
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{ minWidth: 300 }}>
                                  {!item.isTypeOther ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      spacing={2}
                                      sx={{ width: "100%" }}
                                    >
                                      <Typography
                                        sx={{
                                          wordBreak: "break-word",
                                          whiteSpace: "normal",
                                        }}
                                      >
                                        {item.label}
                                      </Typography>

                                      {item.label ===
                                        "Family Protection Coverage - OPCF 44R" && (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                        >
                                          <Typography>
                                            is deductible?
                                          </Typography>
                                          <Checkbox
                                            checked={item.isDeductible}
                                            onChange={handleCheckboxChange(
                                              index
                                            )}
                                          />
                                        </Stack>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Stack
                                      direction="row"
                                      gap={2}
                                      sx={{ width: "100%", display: "flex" }}
                                    >
                                      <AutoComplete
                                        fullWidth
                                        value={item.label}
                                        placeholder="Select one"
                                        options={csioOtherCoverages}
                                        onChange={handleSelectChange(
                                          index,
                                          "label"
                                        )}
                                      />

                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                      >
                                        <Typography>is deductible?</Typography>
                                        <Checkbox
                                          checked={item.isDeductible}
                                          onChange={handleCheckboxChange(index)}
                                        />
                                      </Stack>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {!item.isOnlyPremimum && (
                                    <CustomShrinkTextField
                                      label={
                                        showDeductibleOnly || item.isDeductible
                                          ? "Deductible"
                                          : "Limit"
                                      }
                                      value={
                                        showDeductibleOnly || item.isDeductible
                                          ? item.deductible
                                          : item.limit
                                      }
                                      onChange={handleTextChange(
                                        index,
                                        showDeductibleOnly || item.isDeductible
                                          ? "deductible"
                                          : "limit"
                                      )}
                                      variant="outlined"
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <CustomShrinkTextField
                                    label="Premium"
                                    value={item.premimum}
                                    onChange={handleTextChange(
                                      index,
                                      "premimum"
                                    )}
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <CustomShrinkTextField
                                    label="Driver"
                                    value={item.description}
                                    onChange={handleTextChange(
                                      index,
                                      "description"
                                    )}
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>

                              {item.label ===
                                "Accident Benefits (Standard Benefits)" && (
                                <TableRow>
                                  <TableCell colSpan={5}>
                                    <Typography
                                      fontWeight={600}
                                      variant="h5"
                                      sx={{ padding: "8px 0" }}
                                    >
                                      Optional Increased Accident Benefits
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 2, mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={9}>
                  <Typography variant="h4" fontWeight={600}>
                    Total Premium
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="h4" fontWeight={600}>
                    $ {total}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="h4" fontWeight={600}>
                    $ {totalTwo}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <RoleBasedCustomButton
                  moduleKey={MODULE_IDS.AUTOMOBILE}
                  sx={{ width: "15%" }}
                  disabled={saveLoading}
                  variant="contained"
                  type="submit"
                >
                  Save
                </RoleBasedCustomButton>
              </Box>
            </>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
